import { useCallback, useEffect, useState } from 'react'
import { type FieldErrors, useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import toast from 'react-hot-toast'
import { Link } from 'react-router-dom'
import {
  BotSettings,
  getBotSettings,
  updateBotSettings,
  type CheckoutFulfillmentConfig,
  type TenantCommercialPolicy,
  type VerticalPolicyView,
  type ReengagementConfig,
  DEFAULT_REENGAGEMENT_CONFIG,
} from '@/lib/api/botSettings'
import api from '@/services/api'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import WhatsAppPreview from './WhatsAppPreview'
import {
  SHIPPING_AMOUNT_ERROR,
  formatShippingAmount,
  isValidShippingAmountInput,
  parseShippingAmount,
} from '@/lib/shippingAmount'
import {
  DELIVERY_ZONES_ERROR,
  STAFF_CONSULT_TIMEOUT_ERROR,
  formatDeliveryZones,
  isValidDeliveryZonesInput,
  isValidStaffConsultTimeoutInput,
  parseDeliveryZones,
  parseStaffConsultTimeout,
} from '@/lib/deliveryZones'
import CommercialPolicyCard, { type PolicyViewStatus } from './CommercialPolicyCard'
import ReengagementConfigCard from './ReengagementConfigCard'
import { useAuthStore } from '@/store/authStore'
import { normalizeToVertical, getVerticalProfile, getVerticalCrossSellingCopy } from '@/lib/verticalUI'
import {
  Bot,
  MapPin,
  Settings,
  Zap,
  Truck,
  Minus,
  X,
  Maximize2,
  Minimize2,
  Camera,
  ChevronDown,
  ChevronUp,
  UserCheck,
  Clock,
  ShieldCheck,
  Calendar,
  Store,
} from 'lucide-react'

const BUSINESS_TYPE_OPTIONS = [
  { value: 'retail', label: '🛍️ Retail / Tienda de Moda y Calzado' },
  { value: 'tienda_repuestos', label: '🚗 Tienda de Repuestos / Autopartes' },
  { value: 'restaurante', label: '🍽️ Restaurante / Gastronomía y Delivery' },
  { value: 'bodegon_licoreria', label: '🍷 Bodegón y Licorería Express' },
  { value: 'belleza_estetica', label: '💅 Salón de Belleza, Barbería y Spa' },
  { value: 'ferreteria', label: '🔧 Ferretería y Materiales de Construcción' },
  { value: 'inmobiliaria', label: '🏠 Bienes Raíces e Inmobiliaria' },
  { value: 'turismo_posadas', label: '🌴 Posadas, Hoteles y Turismo' },
  { value: 'salud', label: '🏥 Salud, Clínicas y Laboratorios' },
  { value: 'servicios', label: '💼 Servicios Profesionales y Técnicos' },
  { value: 'tecnologia', label: '💻 Tecnología y Electrónica' },
  { value: 'educacion', label: '📚 Educación y Academias' },
  { value: 'otro', label: '✨ Otro tipo de negocio' },
]

const DEFAULT_CHECKOUT_CONFIG: CheckoutFulfillmentConfig = {
  enabled: false,
  askMethod: true,
  askAddress: true,
  methodPrompt: '¿Vas a retirar en tienda o prefieres delivery/envio?',
  addressPrompt: 'Si es delivery/envio, comparteme tu direccion y una referencia para coordinarlo.',
  pickupInstructions: 'Retiro en tienda disponible, sujeto a confirmacion del pedido.',
  deliveryInstructions: 'Si eliges delivery/envio, te pediremos direccion y referencia.',
  shippingInfo: null,
  shippingZones: [],
  suggestAddons: true,
}

const DEFAULT_ESCALATION_MESSAGE =
  'Te estoy conectando con uno de nuestros ejecutivos de ventas para brindarte una atención más personalizada. _En breve te contactaremos._'
const DEFAULT_MAX_RESPONSES_MESSAGE =
  'Parece que necesitas ayuda más especializada. Te conecto con un ejecutivo de ventas que podrá asistirte mejor.'
const DEFAULT_HANDOFF_RECIPIENT_LABEL = 'ejecutivo de ventas'

const schema = z.object({
  botName: z.string().min(1, 'El nombre del bot es requerido'),
  avatar: z.string().optional().nullable(),
  persona: z.enum(['Professional', 'Friendly', 'Tech']).optional().nullable(),
  welcomeMessage: z.string().optional().nullable(),
  typingSimulation: z.boolean().default(true),
  sendReadReceipts: z.boolean().default(true),
  aiEnabled: z.boolean(),
  tone: z.enum(['formal', 'informal', 'soporte', 'ventas']).default('informal'),
  language: z.enum(['es', 'en', 'pt']).default('es'),
  promptBase: z.string().optional().nullable(),
  model: z.string().optional().nullable(),
  temperature: z.number().min(0).max(2).optional().nullable(),
  maxTokens: z.number().int().positive().optional().nullable(),
  checkoutEnabled: z.boolean().default(false),
  checkoutAskMethod: z.boolean().default(true),
  checkoutAskAddress: z.boolean().default(true),
  checkoutSuggestAddons: z.boolean().default(true),
  checkoutMethodPrompt: z.string().default(DEFAULT_CHECKOUT_CONFIG.methodPrompt),
  checkoutAddressPrompt: z.string().default(DEFAULT_CHECKOUT_CONFIG.addressPrompt),
  checkoutPickupInstructions: z.string().default(DEFAULT_CHECKOUT_CONFIG.pickupInstructions),
  checkoutDeliveryInstructions: z.string().default(DEFAULT_CHECKOUT_CONFIG.deliveryInstructions),
  checkoutShippingInfo: z.string().default(''),
  checkoutShippingZones: z.string().default(''),
  checkoutDeliveryZones: z.string().default('').refine(isValidDeliveryZonesInput, DELIVERY_ZONES_ERROR),
  checkoutStaffConsultTimeout: z.string().default('').refine(isValidStaffConsultTimeoutInput, STAFF_CONSULT_TIMEOUT_ERROR),
  checkoutLocalShippingCost: z.string().default('').refine(isValidShippingAmountInput, SHIPPING_AMOUNT_ERROR),
  checkoutFreeShippingThreshold: z.string().default('').refine(isValidShippingAmountInput, SHIPPING_AMOUNT_ERROR),
  checkoutLocalDeliveryTime: z.string().default(''),
  checkoutDeliveryTime: z.string().default(''),
  checkoutSlaValidationTime: z.string().default(''),
  checkoutOrderWaitWarn: z.string().default(''),
  checkoutOrderWaitCritical: z.string().default(''),
  handoffRecipientLabel: z.string().default(DEFAULT_HANDOFF_RECIPIENT_LABEL),
  escalationMessage: z.string().default(DEFAULT_ESCALATION_MESSAGE),
  maxResponsesMessage: z.string().default(DEFAULT_MAX_RESPONSES_MESSAGE),
})

type FormValues = z.infer<typeof schema>

function parseWaitMinutes(raw: string): number | null {
  const trimmed = raw.trim()
  if (!trimmed) return null
  const value = Number(trimmed)
  if (!Number.isFinite(value) || value <= 0) return null
  return Math.min(1440, Math.max(1, Math.round(value)))
}

const AVATAR_COLORS = [
  { name: 'Verde WhatsApp', value: '#25D366' },
  { name: 'Azul', value: '#0084FF' },
  { name: 'Púrpura', value: '#7B68EE' },
  { name: 'Naranja', value: '#FF6B35' },
  { name: 'Rosa', value: '#FF69B4' },
  { name: 'Amarillo', value: '#FFD700' },
]

const FIELD_LABELS: Partial<Record<string, string>> = {
  botName: 'Nombre del Asistente',
  avatar: 'Color del avatar',
  persona: 'Estilo de atención',
  welcomeMessage: 'Mensaje de bienvenida',
  typingSimulation: 'Simulación de escribiendo',
  sendReadReceipts: 'Confirmación de lectura',
  aiEnabled: 'Estado del asistente',
  tone: 'Tono',
  language: 'Idioma',
  checkoutEnabled: 'Envíos y Entregas',
  checkoutAskMethod: 'Ofrecer retiro en tienda',
  checkoutAskAddress: 'Preguntar dirección de envío',
  checkoutSuggestAddons: 'Sugerir adicionales y complementos (Cross-selling)',
  checkoutShippingInfo: 'Condiciones de envío',
  checkoutShippingZones: 'Zonas de cobertura',
  checkoutDeliveryZones: 'Zonas con costo de delivery propio',
  checkoutStaffConsultTimeout: 'Espera para responder una duda del asistente',
  checkoutLocalDeliveryTime: 'Tiempo estimado de entrega local',
  checkoutDeliveryTime: 'Tiempo estimado de despacho / envío nacional',
  checkoutSlaValidationTime: 'Tiempo de validación de pago (SLA)',
}

function formatShippingZones(zones?: string[] | null): string {
  return (zones ?? []).join('\n')
}

function parseShippingZones(raw: string): string[] {
  return raw
    .split(/[\n,]/)
    .map((zone) => zone.trim())
    .filter(Boolean)
}

function normalizeTone(value: unknown): FormValues['tone'] {
  if (typeof value !== 'string') return 'informal'

  const tone = value.trim().toLowerCase()
  if (tone === 'formal' || tone === 'informal' || tone === 'soporte' || tone === 'ventas') {
    return tone
  }

  if (tone === 'amigable' || tone === 'friendly' || tone === 'casual' || tone === 'warm') {
    return 'informal'
  }

  return 'informal'
}

export default function BotConfigForm({ initialBusinessType }: { initialBusinessType?: string } = {}) {
  const authBusinessType = useAuthStore((s) => s.tenant?.businessType)
  const [loading, setLoading] = useState(false)
  const [validationSummary, setValidationSummary] = useState<string[]>([])
  const [previewMinimized, setPreviewMinimized] = useState(false)
  const [previewExpanded, setPreviewExpanded] = useState(false)
  const [showAdvancedCheckout, setShowAdvancedCheckout] = useState(false)
  const [commercialPolicy, setCommercialPolicy] = useState<TenantCommercialPolicy | null>(null)
  const [policyView, setPolicyView] = useState<VerticalPolicyView | null>(null)
  const [policyStatus, setPolicyStatus] = useState<PolicyViewStatus>('loading')
  const [reengagementConfig, setReengagementConfig] = useState<ReengagementConfig>(DEFAULT_REENGAGEMENT_CONFIG)
  const [businessType, setBusinessType] = useState<string>(initialBusinessType || authBusinessType || '')

  const { register, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = useForm<FormValues>({
    resolver: zodResolver(schema as any),
    shouldUnregister: false,
    defaultValues: {
      botName: 'Mi Bot',
      avatar: '#25D366',
      persona: 'Friendly',
      welcomeMessage: '¡Hola! 👋 Bienvenido a nuestra tienda. ¿En qué puedo ayudarte hoy?',
      typingSimulation: true,
      sendReadReceipts: true,
      aiEnabled: false,
      tone: 'informal',
      language: 'es',
      model: 'deepseek/deepseek-chat-v3-0324:free',
      temperature: 0.7,
      maxTokens: 280,
      promptBase: '',
      checkoutEnabled: false,
      checkoutAskMethod: true,
      checkoutAskAddress: true,
      checkoutSuggestAddons: true,
      checkoutMethodPrompt: DEFAULT_CHECKOUT_CONFIG.methodPrompt,
      checkoutAddressPrompt: DEFAULT_CHECKOUT_CONFIG.addressPrompt,
      checkoutPickupInstructions: DEFAULT_CHECKOUT_CONFIG.pickupInstructions,
      checkoutDeliveryInstructions: DEFAULT_CHECKOUT_CONFIG.deliveryInstructions,
      checkoutShippingInfo: '',
      checkoutShippingZones: '',
      checkoutDeliveryZones: '',
      checkoutStaffConsultTimeout: '',
      checkoutLocalShippingCost: '',
      checkoutFreeShippingThreshold: '',
      checkoutLocalDeliveryTime: '',
      checkoutDeliveryTime: '',
      checkoutSlaValidationTime: '',
      checkoutOrderWaitWarn: '',
      checkoutOrderWaitCritical: '',
      handoffRecipientLabel: DEFAULT_HANDOFF_RECIPIENT_LABEL,
      escalationMessage: DEFAULT_ESCALATION_MESSAGE,
      maxResponsesMessage: DEFAULT_MAX_RESPONSES_MESSAGE,
    },
  })

  const botName = watch('botName')
  const avatar = watch('avatar')
  const welcomeMessage = watch('welcomeMessage')
  const typingSimulation = watch('typingSimulation')
  const checkoutEnabled = watch('checkoutEnabled')
  const handoffRecipientLabel = watch('handoffRecipientLabel')

  const normalizedVertical = normalizeToVertical(businessType)
  const profile = getVerticalProfile(normalizedVertical)
  // Si el backend ya informó el vertical, su `requiresAppointment` manda sobre el registro local del frontend.
  const hasFulfillment = policyView ? !policyView.runtime.requiresAppointment : profile.operational.hasFulfillment
  const crossSellingCopy = getVerticalCrossSellingCopy(normalizedVertical)

  const loadSettings = useCallback(
    (isActive: () => boolean = () => true) => {
    setLoading(true)
    setPolicyStatus('loading')

    return Promise.all([
      getBotSettings(),
      api.get('/tenants/current').catch(() => ({ data: null })),
    ])
      .then(([data, tenantRes]) => {
        if (!isActive()) return

        if (tenantRes?.data?.businessType) {
          setBusinessType(tenantRes.data.businessType)
        }
        if (data.commercialPolicyConfig) {
          setCommercialPolicy(data.commercialPolicyConfig)
        }
        setPolicyView(data.commercialPolicyView ?? null)
        setPolicyStatus(data.commercialPolicyView ? 'ready' : 'error')
        if (data.reengagementConfig) {
          setReengagementConfig(data.reengagementConfig)
        }

        const checkoutConfig = data.checkoutConfig ?? DEFAULT_CHECKOUT_CONFIG

        setValue('botName', data.botName || 'Mi Bot')
        setValue('avatar', data.avatar || '#25D366')
        setValue('persona', data.persona || 'Friendly')
        setValue('welcomeMessage', data.welcomeMessage || '¡Hola! 👋 Bienvenido a nuestra tienda. ¿En qué puedo ayudarte hoy?')
        setValue('typingSimulation', data.typingSimulation ?? true)
        setValue('sendReadReceipts', data.sendReadReceipts ?? true)
        setValue('aiEnabled', !!data.aiEnabled)
        setValue('tone', normalizeTone(data.tone))
        setValue('language', (data.language as any) || 'es')
        setValue('promptBase', data.promptBase ?? '')
        setValue('model', data.model ?? 'deepseek/deepseek-chat-v3-0324:free')
        setValue('temperature', (data.temperature as any) ?? 0.7)
        setValue('maxTokens', (data.maxTokens as any) ?? 280)
        setValue('checkoutEnabled', checkoutConfig.enabled ?? false)
        setValue('checkoutAskMethod', checkoutConfig.askMethod ?? true)
        setValue('checkoutAskAddress', checkoutConfig.askAddress ?? true)
        setValue('checkoutSuggestAddons', checkoutConfig.suggestAddons ?? true)
        setValue('checkoutMethodPrompt', checkoutConfig.methodPrompt || DEFAULT_CHECKOUT_CONFIG.methodPrompt)
        setValue('checkoutAddressPrompt', checkoutConfig.addressPrompt || DEFAULT_CHECKOUT_CONFIG.addressPrompt)
        setValue('checkoutPickupInstructions', checkoutConfig.pickupInstructions || DEFAULT_CHECKOUT_CONFIG.pickupInstructions)
        setValue('checkoutDeliveryInstructions', checkoutConfig.deliveryInstructions || DEFAULT_CHECKOUT_CONFIG.deliveryInstructions)
        setValue('checkoutShippingInfo', checkoutConfig.shippingInfo ?? '')
        setValue('checkoutShippingZones', formatShippingZones(checkoutConfig.shippingZones))
        setValue('checkoutDeliveryZones', formatDeliveryZones(checkoutConfig.deliveryZones))
        setValue('checkoutStaffConsultTimeout', checkoutConfig.staffConsultTimeoutMinutes ? String(checkoutConfig.staffConsultTimeoutMinutes) : '')
        setValue('checkoutLocalShippingCost', formatShippingAmount(checkoutConfig.localShippingCost))
        setValue('checkoutFreeShippingThreshold', formatShippingAmount(checkoutConfig.freeShippingThreshold))
        setValue('checkoutLocalDeliveryTime', checkoutConfig.localDeliveryTime ?? '')
        setValue('checkoutDeliveryTime', checkoutConfig.deliveryTime ?? '')
        setValue('checkoutSlaValidationTime', checkoutConfig.slaValidationTime ?? '')
        setValue('checkoutOrderWaitWarn', checkoutConfig.orderWaitWarnMinutes ? String(checkoutConfig.orderWaitWarnMinutes) : '')
        setValue('checkoutOrderWaitCritical', checkoutConfig.orderWaitCriticalMinutes ? String(checkoutConfig.orderWaitCriticalMinutes) : '')
        setValue('handoffRecipientLabel', (data as any).handoffRecipientLabel || DEFAULT_HANDOFF_RECIPIENT_LABEL)
        setValue('escalationMessage', (data as any).escalationMessage || DEFAULT_ESCALATION_MESSAGE)
        setValue('maxResponsesMessage', (data as any).maxResponsesMessage || DEFAULT_MAX_RESPONSES_MESSAGE)
      })
      .catch((err) => {
        console.error(err)
        setPolicyStatus('error')
        toast.error('No se pudieron cargar las configuraciones')
      })
      .finally(() => setLoading(false))
    },
    [setValue]
  )

  useEffect(() => {
    let mounted = true
    void loadSettings(() => mounted)
    return () => {
      mounted = false
    }
  }, [loadSettings])

  // Tras cambiar de vertical solo se refresca la vista de políticas: recargar todo pisaría lo que el usuario aún no guardó.
  const refreshPolicyView = useCallback(async () => {
    setPolicyStatus('loading')
    try {
      const data = await getBotSettings()
      setPolicyView(data.commercialPolicyView ?? null)
      setPolicyStatus(data.commercialPolicyView ? 'ready' : 'error')
    } catch {
      setPolicyStatus('error')
    }
  }, [])

  const handleBusinessTypeChange = async (newType: string) => {
    try {
      setBusinessType(newType)
      await api.patch('/tenants/me', { businessType: newType })
      const authState = useAuthStore.getState()
      if (authState.tenant) {
        authState.login({
          token: authState.token!,
          tenantId: authState.tenantId,
          user: authState.user,
          tenant: { ...authState.tenant, businessType: newType },
        })
      }
      toast.success('Vertical de negocio actualizada')
      await refreshPolicyView()
    } catch (err) {
      console.error('Error al actualizar vertical de negocio:', err)
      toast.error('No se pudo actualizar el tipo de negocio')
    }
  }

  const onSubmit = async (values: FormValues) => {
    try {
      setValidationSummary([])
      const payload: BotSettings = {
        botName: values.botName,
        avatar: values.avatar,
        persona: values.persona,
        welcomeMessage: values.welcomeMessage,
        typingSimulation: values.typingSimulation,
        sendReadReceipts: values.sendReadReceipts,
        aiEnabled: values.aiEnabled,
        tone: values.tone,
        language: values.language,
        promptBase: values.promptBase ?? null,
        model: values.model ?? null,
        temperature: values.temperature ?? null,
        maxTokens: values.maxTokens ?? null,
        checkoutConfig: {
          enabled: values.checkoutEnabled,
          askMethod: values.checkoutAskMethod,
          askAddress: values.checkoutAskAddress,
          suggestAddons: values.checkoutSuggestAddons,
          methodPrompt: values.checkoutMethodPrompt.trim() || DEFAULT_CHECKOUT_CONFIG.methodPrompt,
          addressPrompt: values.checkoutAddressPrompt.trim() || DEFAULT_CHECKOUT_CONFIG.addressPrompt,
          pickupInstructions: values.checkoutPickupInstructions.trim() || DEFAULT_CHECKOUT_CONFIG.pickupInstructions,
          deliveryInstructions: values.checkoutDeliveryInstructions.trim() || DEFAULT_CHECKOUT_CONFIG.deliveryInstructions,
          shippingInfo: values.checkoutShippingInfo.trim() ? values.checkoutShippingInfo.trim() : null,
          shippingZones: parseShippingZones(values.checkoutShippingZones),
          deliveryZones: parseDeliveryZones(values.checkoutDeliveryZones),
          staffConsultTimeoutMinutes: parseStaffConsultTimeout(values.checkoutStaffConsultTimeout),
          localShippingCost: parseShippingAmount(values.checkoutLocalShippingCost),
          freeShippingThreshold: parseShippingAmount(values.checkoutFreeShippingThreshold),
          localDeliveryTime: values.checkoutLocalDeliveryTime.trim() ? values.checkoutLocalDeliveryTime.trim() : null,
          deliveryTime: values.checkoutDeliveryTime.trim() ? values.checkoutDeliveryTime.trim() : null,
          slaValidationTime: values.checkoutSlaValidationTime.trim() ? values.checkoutSlaValidationTime.trim() : null,
          orderWaitWarnMinutes: parseWaitMinutes(values.checkoutOrderWaitWarn),
          orderWaitCriticalMinutes: parseWaitMinutes(values.checkoutOrderWaitCritical),
        },
        commercialPolicyConfig: commercialPolicy,
        reengagementConfig,
        handoffRecipientLabel: values.handoffRecipientLabel.trim() || DEFAULT_HANDOFF_RECIPIENT_LABEL,
        escalationMessage: values.escalationMessage.trim() || null,
        maxResponsesMessage: values.maxResponsesMessage.trim() || null,
      }
      await updateBotSettings(payload)
      toast.success('Configuración guardada exitosamente')
    } catch (err) {
      console.error(err)
      toast.error('Error al guardar la configuración')
    }
  }

  const handleInvalidSubmit = (formErrors: FieldErrors<FormValues>) => {
    const summary = Object.entries(formErrors)
      .map(([field, fieldError]) => {
        const label = FIELD_LABELS[field] ?? field
        return fieldError?.message ? `${label}: ${String(fieldError.message)}` : label
      })
      .filter(Boolean)

    setValidationSummary(summary)

    const firstField = Object.keys(formErrors)[0]
    if (firstField) {
      const el = document.querySelector(`[name="${firstField}"]`) as HTMLElement | null
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      el?.focus?.()
    }
  }

  const submitForm = handleSubmit(onSubmit, handleInvalidSubmit)

  return (
    <div className="flex flex-col xl:flex-row items-start gap-8 w-full pb-8">
      <div className="flex-1 w-full max-w-4xl min-w-0">
        <form
          onSubmit={submitForm}
          className="space-y-6"
        >
          {validationSummary.length > 0 && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 shadow-sm">
              <p className="font-semibold mb-2">Revisa estos campos antes de guardar:</p>
              <ul className="space-y-1 list-disc pl-5">
                {validationSummary.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Switch General de Activación */}
          <div className="flex items-center justify-between gap-4 p-4 sm:p-5 rounded-2xl bg-gradient-to-r from-[#075E54]/10 via-[#25D366]/5 to-emerald-50 border border-[#075E54]/20 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-br from-[#075E54] to-[#25D366] rounded-xl flex items-center justify-center shadow flex-shrink-0">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-base font-bold text-gray-900">Asistente de Ventas Inteligente</p>
                <p className="text-xs text-gray-600 mt-0.5">Respuestas automáticas, asesoría comercial y catálogo 24/7</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
              <input type="checkbox" {...register('aiEnabled')} className="sr-only peer" />
              <div className="w-12 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#075E54]/20 rounded-full peer peer-checked:after:translate-x-6 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#075E54]" />
              <span className={cn('ml-2.5 text-xs font-bold', watch('aiEnabled') ? 'text-[#075E54]' : 'text-gray-400')}>
                {watch('aiEnabled') ? 'Activo' : 'Inactivo'}
              </span>
            </label>
          </div>

          {/* Badge Destacado: Búsqueda Visual y Multimodal Activa */}
          <div className="p-4 rounded-2xl border border-sky-200 bg-gradient-to-r from-sky-50 to-indigo-50/50 flex items-start gap-3 shadow-sm">
            <div className="w-9 h-9 rounded-xl bg-sky-600 flex items-center justify-center flex-shrink-0 shadow-sm text-white">
              <Camera className="w-5 h-5" />
            </div>
            <div className="text-xs sm:text-sm text-sky-950">
              <p className="font-bold">Reconocimiento Visual por Fotos Activo 📸</p>
              <p className="text-sky-800 text-xs mt-0.5 leading-relaxed">
                Tus clientes pueden enviarte fotos de prendas o productos de referencia por WhatsApp y el asistente los reconocerá al instante en tu inventario gracias a la inteligencia multimodal.
              </p>
            </div>
          </div>

          {/* Sección 1: Identidad del Bot */}
          <section className="bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 shadow-sm space-y-5">
            <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
              <Bot className="w-4 h-4 text-[#075E54]" />
              <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Identidad del Asistente</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre del Asistente</label>
                <input
                  {...register('botName')}
                  type="text"
                  className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none transition bg-white"
                  placeholder="Ej: Sofía de Mi Tienda"
                />
                {errors.botName && <p className="text-xs text-red-500 mt-1">{errors.botName.message}</p>}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1.5">Color del Avatar</label>
                <div className="flex gap-2.5 flex-wrap pt-1">
                  {AVATAR_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={() => setValue('avatar', color.value)}
                      title={color.name}
                      className={cn(
                        'w-8 h-8 rounded-full border-2 transition-all hover:scale-110',
                        avatar === color.value
                          ? 'border-[#075E54] ring-2 ring-[#075E54] ring-offset-2 scale-110'
                          : 'border-transparent',
                      )}
                      style={{ backgroundColor: color.value }}
                    />
                  ))}
                </div>
                <input type="hidden" {...register('avatar')} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Estilo de Asesoría de Ventas</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  {
                    id: 'Friendly',
                    label: 'Asesor Consultivo ⭐',
                    icon: '🤝',
                    desc: 'Pregunta necesidades, compara y sugiere combinaciones de valor.',
                  },
                  {
                    id: 'Professional',
                    label: 'Profesional / Formal',
                    icon: '💼',
                    desc: 'Formal, sobrio y ejecutivo. Ideal para servicios o B2B.',
                  },
                  {
                    id: 'Tech',
                    label: 'Ágil y Directo',
                    icon: '⚡',
                    desc: 'Respuestas cortas enfocadas en agilizar el cierre del pedido.',
                  },
                ].map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setValue('persona', item.id as any)}
                    className={cn(
                      'p-3.5 rounded-2xl border-2 transition-all text-left flex flex-col justify-between',
                      watch('persona') === item.id
                        ? 'border-[#075E54] bg-[#075E54]/5 ring-1 ring-[#075E54]'
                        : 'border-gray-200 hover:border-gray-300 bg-white',
                    )}
                  >
                    <div>
                      <span className="text-xl">{item.icon}</span>
                      <p className="text-xs font-bold text-gray-900 mt-1">{item.label}</p>
                      <p className="text-[11px] text-gray-500 mt-1 leading-relaxed">{item.desc}</p>
                    </div>
                  </button>
                ))}
              </div>
              <input type="hidden" {...register('persona')} />
            </div>
          </section>

          {/* Sección 2: Comportamiento y Bienvenida */}
          <section className="bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 shadow-sm space-y-4">
            <div className="flex items-center gap-2 pb-3 border-b border-gray-100">
              <Settings className="w-4 h-4 text-[#075E54]" />
              <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest">Saludo y Comportamiento</h2>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Mensaje de Bienvenida</label>
              <textarea
                {...register('welcomeMessage')}
                rows={3}
                className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none transition bg-white"
                placeholder="¡Hola! 👋 Bienvenido. ¿En qué podemos ayudarte hoy?"
              />
              <p className="text-xs text-gray-400 mt-1">Primer mensaje que recibe un cliente nuevo al escribir a tu WhatsApp.</p>
            </div>

            <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-gray-50 border border-gray-100">
              <div>
                <p className="text-sm font-semibold text-gray-800">Simular &quot;Escribiendo...&quot;</p>
                <p className="text-xs text-gray-400 mt-0.5">Muestra el indicador de escritura de WhatsApp antes de responder para mayor naturalidad.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" {...register('typingSimulation')} className="sr-only peer" />
                <div className="w-10 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#075E54]/20 rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#075E54]" />
              </label>
            </div>

            <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-gray-50 border border-gray-100">
              <div>
                <p className="text-sm font-semibold text-gray-800">Confirmar lectura (doble check azul)</p>
                <p className="text-xs text-gray-400 mt-0.5">Marca los mensajes como leídos unos segundos después de recibirlos, como lo haría una persona. Si lo desactivas, el cliente no verá el doble check azul.</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" {...register('sendReadReceipts')} className="sr-only peer" aria-label="Confirmar lectura en WhatsApp" />
                <div className="w-10 h-5 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#075E54]/20 rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#075E54]" />
              </label>
            </div>
          </section>

          {/* Acceso Directo a Derivación Humana (Sin Duplicación de Campos) */}
          <div className="p-4 rounded-2xl border border-orange-100 bg-orange-50/50 flex items-center justify-between gap-3 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-orange-500 flex items-center justify-center text-white shadow-sm flex-shrink-0">
                <UserCheck className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">Atención y Derivación Humana</p>
                <p className="text-xs text-gray-500">
                  Actualmente atendido por: <strong className="text-gray-800">{handoffRecipientLabel || 'asesor de ventas'}</strong>
                </p>
              </div>
            </div>
            <Link
              to="/app/bot-settings/escalation"
              className="text-xs font-semibold text-orange-700 bg-white border border-orange-200 rounded-xl px-3 py-1.5 hover:bg-orange-100/60 transition shadow-sm flex-shrink-0"
            >
              Configurar →
            </Link>
          </div>

          {/* Sección 3: Envíos y Entregas (Checkout Simplificado) */}
          {hasFulfillment ? (
            <section className="space-y-4 rounded-2xl border border-emerald-100 bg-gradient-to-br from-white to-emerald-50/30 p-5 sm:p-6 shadow-sm">
              <div className="flex items-start justify-between gap-4 pb-2 border-b border-emerald-100/60">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#075E54] flex items-center justify-center shadow-sm">
                    <Truck className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900">Envíos y Retiros en Tienda (Checkout)</h2>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Permite al asistente coordinar la entrega con el cliente antes de cerrar el pedido.{' '}
                      <Link to="/app/bot-settings/payments" className="font-semibold text-[#075E54] underline">
                        Cómo cobras y si el asistente envía datos bancarios
                      </Link>
                    </p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
                  <input type="checkbox" {...register('checkoutEnabled')} className="sr-only peer" />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:ring-2 peer-focus:ring-[#075E54]/20 rounded-full peer peer-checked:after:translate-x-5 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#075E54]" />
                  <span className={cn('ml-2 text-xs font-bold', checkoutEnabled ? 'text-emerald-700' : 'text-gray-400')}>
                    {checkoutEnabled ? 'Activo' : 'Inactivo'}
                  </span>
                </label>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <label className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-white p-3.5 shadow-sm cursor-pointer hover:bg-emerald-50/30 transition">
                  <input type="checkbox" {...register('checkoutAskMethod')} className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                  <span>
                    <span className="block text-xs font-bold text-gray-900">Ofrecer retiro en tienda / local</span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">El bot consultará si retira personalmente o prefiere envío.</span>
                  </span>
                </label>

                <label className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-white p-3.5 shadow-sm cursor-pointer hover:bg-emerald-50/30 transition">
                  <input type="checkbox" {...register('checkoutAskAddress')} className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500" />
                  <span>
                    <span className="block text-xs font-bold text-gray-900">Solicitar dirección para delivery</span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">Pide dirección y punto de referencia solo si elige delivery.</span>
                  </span>
                </label>

                <label className="flex items-start gap-3 rounded-2xl border border-emerald-100 bg-white p-3.5 shadow-sm cursor-pointer hover:bg-emerald-50/30 transition sm:col-span-2">
                  <input
                    type="checkbox"
                    {...register('checkoutSuggestAddons', {
                      onChange: (e) => {
                        const checked = Boolean(e.target.checked)
                        setCommercialPolicy((prev) => {
                          const isRep = normalizeToVertical(businessType) === 'tienda_repuestos'
                          if (isRep) {
                            return {
                              ...prev,
                              schemaVersion: 1,
                              repuestos: { ...(prev?.repuestos ?? {}), crossSellingEnabled: checked },
                            }
                          }
                          return {
                            ...prev,
                            schemaVersion: 1,
                            generic: { ...(prev?.generic ?? {}), crossSellingEnabled: checked },
                          }
                        })
                      },
                    })}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                  />
                  <span>
                    <span className="block text-xs font-bold text-gray-900 flex items-center gap-1.5">
                      <Zap className="w-3.5 h-3.5 text-amber-500" />
                      {crossSellingCopy.title}
                    </span>
                    <span className="block text-[11px] text-gray-500 mt-0.5">
                      {crossSellingCopy.description} (Gestiona las reglas de insumos o complementos en la Sección de Políticas abajo).
                    </span>
                  </span>
                </label>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5 text-emerald-600" />
                    Zonas o ciudades de cobertura
                  </label>
                  <textarea
                    {...register('checkoutShippingZones')}
                    rows={2}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none bg-white"
                    placeholder="Ej: Caracas, Valencia, Maracay (o Todo el país)"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                    Tarifas o condiciones de entrega
                  </label>
                  <textarea
                    {...register('checkoutShippingInfo')}
                    rows={2}
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none bg-white"
                    placeholder="Ej: Envíos gratis a partir de $50 o tarifa fija $3."
                  />
                </div>
              </div>

              <div>
                <label htmlFor="checkoutDeliveryZones" className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                  Zonas con costo de delivery propio
                </label>
                <textarea
                  id="checkoutDeliveryZones"
                  {...register('checkoutDeliveryZones')}
                  rows={3}
                  aria-describedby="checkoutDeliveryZonesHelp"
                  className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none resize-none bg-white"
                  placeholder={'Una zona por línea, con su costo en USD:\nLos Palos Grandes: 5\nPetare: 7'}
                />
                <p id="checkoutDeliveryZonesHelp" className="text-[11px] text-gray-500 mt-1">
                  Si el cliente da una dirección que no está en tus zonas, el asistente no inventa el costo: te avisa para que lo respondas desde el chat.
                </p>
                {errors.checkoutDeliveryZones && (
                  <p role="alert" className="text-xs text-red-500 mt-1">{errors.checkoutDeliveryZones.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="checkoutStaffConsultTimeout" className="block text-xs font-semibold text-gray-700 mb-1">
                  Minutos de espera para responder una duda del asistente
                </label>
                <input
                  id="checkoutStaffConsultTimeout"
                  {...register('checkoutStaffConsultTimeout')}
                  type="text"
                  inputMode="numeric"
                  aria-describedby="checkoutStaffConsultTimeoutHelp"
                  className="w-32 rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none bg-white"
                  placeholder="30"
                />
                <p id="checkoutStaffConsultTimeoutHelp" className="text-[11px] text-gray-500 mt-1">
                  Si nadie responde a tiempo, el asistente le avisa al cliente que seguimos confirmando. Vacío = 30 minutos.
                </p>
                {errors.checkoutStaffConsultTimeout && (
                  <p role="alert" className="text-xs text-red-500 mt-1">{errors.checkoutStaffConsultTimeout.message}</p>
                )}
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label htmlFor="checkoutLocalShippingCost" className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5 text-emerald-600" />
                    Costo de delivery local (USD)
                  </label>
                  <input
                    id="checkoutLocalShippingCost"
                    {...register('checkoutLocalShippingCost')}
                    type="text"
                    inputMode="decimal"
                    aria-describedby="checkoutShippingCostHelp"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none bg-white"
                    placeholder="Ej: 3.50"
                  />
                  {errors.checkoutLocalShippingCost && (
                    <p className="text-xs text-red-500 mt-1">{errors.checkoutLocalShippingCost.message}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="checkoutFreeShippingThreshold" className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5 text-emerald-600" />
                    Delivery gratis desde (USD)
                  </label>
                  <input
                    id="checkoutFreeShippingThreshold"
                    {...register('checkoutFreeShippingThreshold')}
                    type="text"
                    inputMode="decimal"
                    aria-describedby="checkoutShippingCostHelp"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none bg-white"
                    placeholder="Ej: 50"
                  />
                  {errors.checkoutFreeShippingThreshold && (
                    <p className="text-xs text-red-500 mt-1">{errors.checkoutFreeShippingThreshold.message}</p>
                  )}
                </div>
              </div>
              <p id="checkoutShippingCostHelp" className="text-xs text-gray-500">
                Si dejas el costo vacío, el bot no citará ningún monto de envío y avisará al cliente que el equipo lo confirma.
              </p>

              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Clock className="w-3.5 h-3.5 text-emerald-600" />
                    Entrega local (delivery)
                  </label>
                  <input
                    {...register('checkoutLocalDeliveryTime')}
                    type="text"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none bg-white"
                    placeholder="Ej: 30 a 45 minutos"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5 text-emerald-600" />
                    Despacho / Envío nacional
                  </label>
                  <input
                    {...register('checkoutDeliveryTime')}
                    type="text"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none bg-white"
                    placeholder="Ej: 24 a 48 horas hábiles"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1 flex items-center gap-1.5">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                    Validación de pago (SLA)
                  </label>
                  <input
                    {...register('checkoutSlaValidationTime')}
                    type="text"
                    className="w-full rounded-xl border border-gray-200 px-3 py-2 text-xs focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none bg-white"
                    placeholder="Ej: 15 a 30 minutos"
                  />
                </div>
              </div>

              <div className="mt-3 rounded-xl border border-gray-200 bg-white p-3">
                <p className="text-xs font-semibold text-gray-700">Cronómetro de comandas</p>
                <p className="mt-0.5 text-xs text-gray-500">
                  Solo aplica al tablero de comandas (restaurantes y bodegones). La tarjeta cambia a ámbar y luego a rojo
                  cuando una comanda lleva demasiado tiempo esperando.
                </p>
                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                  <div>
                    <label htmlFor="checkout-order-wait-warn" className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                      <Clock className="h-3.5 w-3.5 text-emerald-600" />
                      Aviso de espera (minutos)
                    </label>
                    <input
                      id="checkout-order-wait-warn"
                      {...register('checkoutOrderWaitWarn')}
                      type="number"
                      min={1}
                      max={1440}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs outline-none focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20"
                      placeholder="Ej: 15"
                    />
                  </div>
                  <div>
                    <label htmlFor="checkout-order-wait-critical" className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-gray-700">
                      <Clock className="h-3.5 w-3.5 text-rose-600" />
                      Alerta de espera (minutos)
                    </label>
                    <input
                      id="checkout-order-wait-critical"
                      {...register('checkoutOrderWaitCritical')}
                      type="number"
                      min={1}
                      max={1440}
                      className="w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs outline-none focus:border-[#075E54] focus:ring-2 focus:ring-[#075E54]/20"
                      placeholder="Ej: 30"
                    />
                  </div>
                </div>
              </div>

              {/* Ajustes avanzados de checkout colapsables */}
              <div className="rounded-xl border border-gray-200 bg-white overflow-hidden mt-2">
                <button
                  type="button"
                  onClick={() => setShowAdvancedCheckout(!showAdvancedCheckout)}
                  className="w-full flex items-center justify-between px-4 py-2.5 text-left text-xs font-semibold text-gray-600 hover:bg-gray-50 transition"
                >
                  <span>⚙️ Personalizar frases del proceso de entrega (Opcional)</span>
                  {showAdvancedCheckout ? <ChevronUp className="w-3.5 h-3.5 text-gray-400" /> : <ChevronDown className="w-3.5 h-3.5 text-gray-400" />}
                </button>

                {showAdvancedCheckout && (
                  <div className="p-4 border-t border-gray-100 space-y-3 bg-gray-50/50">
                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 mb-1">Pregunta de método</label>
                      <input
                        {...register('checkoutMethodPrompt')}
                        type="text"
                        className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs bg-white"
                        placeholder="¿Vas a retirar en tienda o prefieres delivery/envio?"
                      />
                    </div>

                    <div>
                      <label className="block text-[11px] font-semibold text-gray-700 mb-1">Pregunta de dirección</label>
                      <input
                        {...register('checkoutAddressPrompt')}
                        type="text"
                        className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-xs bg-white"
                        placeholder="Si es delivery/envio, comparteme tu direccion..."
                      />
                    </div>
                  </div>
                )}
              </div>
            </section>
          ) : (
            <div className="p-4 rounded-2xl border border-blue-100 bg-blue-50/50 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-sm flex-shrink-0">
                <Calendar className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm font-bold text-gray-900">Atención Presencial y Citas ({profile.label})</p>
                <p className="text-xs text-gray-600 mt-0.5">
                  Este vertical no gestiona despachos físicos ni delivery. La atención se coordina mediante citas y turnos en estado pendiente.
                </p>
              </div>
            </div>
          )}

          {/* Selector de Giro Comercial / Vertical del Negocio */}
          <div className="bg-white rounded-2xl border border-gray-200/80 p-5 sm:p-6 shadow-sm space-y-3">
            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <Store className="w-4 h-4 text-[#075E54]" />
                <h2 className="text-xs font-bold text-gray-500 uppercase tracking-widest">
                  Giro Comercial y Vertical de Negocio
                </h2>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 bg-[#075E54]/10 text-[#075E54] rounded-full">
                {profile.label}
              </span>
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Selecciona el sector de tu negocio para que el asistente aplique la psicología de ventas, reglas de cierre y políticas especializadas de tu industria.
            </p>
            <div className="pt-1">
              <select
                value={businessType || 'otro'}
                onChange={(e) => void handleBusinessTypeChange(e.target.value)}
                className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-[#075E54]/20 focus:border-[#075E54] outline-none transition bg-white text-gray-800 font-medium"
              >
                {BUSINESS_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Sección 4: Políticas Comerciales de Negocio por Vertical */}
          <CommercialPolicyCard
            view={policyView}
            status={policyStatus}
            onRetry={() => void refreshPolicyView()}
            policy={commercialPolicy}
            onChange={(updated) => {
              setCommercialPolicy(updated)
              const vertical = normalizeToVertical(businessType)
              const isEnabled = Boolean(
                vertical === 'tienda_repuestos'
                  ? updated.repuestos?.crossSellingEnabled
                  : (updated.generic?.crossSellingEnabled ?? updated.repuestos?.crossSellingEnabled)
              )
              setValue('checkoutSuggestAddons', isEnabled)
            }}
          />

          {/* Sección 5: Recuperación Automática de Leads */}
          <ReengagementConfigCard
            config={reengagementConfig}
            onChange={setReengagementConfig}
            disabled={isSubmitting || loading}
          />

          <div className="flex justify-end pt-2 pb-8">
            <Button
              type="button"
              onClick={() => void submitForm()}
              disabled={isSubmitting || loading}
              className="bg-[#075E54] hover:bg-[#064E46] text-white px-8 py-3 text-sm font-semibold shadow-md hover:shadow-lg transition-all rounded-xl"
            >
              {isSubmitting ? 'Guardando...' : 'Guardar cambios'}
            </Button>
          </div>
        </form>
      </div>

      {/* Simulador interactivo de WhatsApp a la derecha */}
      <div className={cn(
        "hidden xl:flex flex-col border border-gray-200/80 bg-white rounded-2xl shadow-xl overflow-hidden transition-all duration-300 sticky top-4 self-start max-h-[calc(100vh-2rem)]",
        previewMinimized ? "w-[320px] h-[56px]" : "w-[380px] flex-shrink-0",
        previewExpanded ? "fixed inset-4 z-50 w-auto h-auto" : ""
      )}>
        <div className="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse" />
            <p className="text-sm font-bold text-gray-800">Simulador de WhatsApp</p>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPreviewMinimized(!previewMinimized)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
              title={previewMinimized ? "Maximizar" : "Minimizar"}
            >
              {previewMinimized ? <Maximize2 className="w-4 h-4" /> : <Minus className="w-4 h-4" />}
            </button>
            <button
              onClick={() => setPreviewExpanded(!previewExpanded)}
              className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
              title={previewExpanded ? "Restaurar" : "Expandir"}
            >
              {previewExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            {previewMinimized && (
              <button
                onClick={() => {
                  setPreviewMinimized(false)
                  setPreviewExpanded(false)
                }}
                className="p-1.5 hover:bg-red-50 rounded-lg transition-colors text-gray-400 hover:text-red-500"
                title="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        <p className="px-4 py-2 text-xs text-gray-400 bg-gray-50 border-b border-gray-100">
          Chatea aquí para probar las respuestas en vivo de tu asistente
        </p>
        {!previewMinimized && (
          <div className="flex-1 flex items-center justify-center p-5 bg-gray-50/50">
            <WhatsAppPreview
              botName={botName || 'Mi Bot'}
              avatar={avatar}
              welcomeMessage={welcomeMessage}
              typingSimulation={typingSimulation}
              interactive
            />
          </div>
        )}
      </div>
    </div>
  )
}
